import { useMemo, useRef, useState } from "react";
import api from "../api";
import { defaultMonth, inMonth, monthLabel, monthsOf } from "../lib/carcassMonths";
import { reportUrl } from "../lib/vectorsurv";
import CarcassWatchStatus from "./CarcassWatchStatus";

/**
 * Dead bird reports, sorted by whether they are the District's (ROADMAP B12).
 *
 * Upload the report export from VectorSurv and every report lands in one of
 * three tables: inside the District, outside it, or needs a look. The third
 * is not a hedge. A report that cannot be placed with confidence is not the
 * same as one that is outside, and folding it into "outside" would quietly
 * drop a report that was hers.
 */

interface Row {
    verdict: "inside" | "outside" | "needs a look";
    method: string;
    lat: number | null;
    lng: number | null;
    miles_from_boundary: number | null;
    near_edge: boolean;
    matched_address: string;
    note: string;
    id: string;
    date: string;
    species: string;
    /** VectorSurv's disposition of the call ("Closed Zip Code", "Species Not
     *  Accepted"...), not the state of the bird, despite the heading. */
    condition: string;
    status: string;
    wnv: string;
    agency: string;
    parent: string;
    cross: string;
    number: string;
    county: string;
    address: string;
    city: string;
    zip: string;
}

interface Result {
    rows: Row[];
    counts: { inside: number; outside: number; "needs a look": number; near_edge: number };
    columns: Record<string, string>;
    fieldnames: string[];
}

const ROLE_LABEL: Record<string, string> = {
    lat: "latitude", lng: "longitude", address: "address", city: "city",
    zip: "ZIP", id: "report", date: "date", species: "species",
    condition: "disposition", status: "status", wnv: "WNV", agency: "submitted by",
    parent: "follow-up of", cross: "cross streets", number: "birds", county: "county",
};

function whereText(r: Row) {
    if (r.lat === null || r.lng === null) return "Not placed";
    const how = r.method === "address" ? "from the address" : "from the coordinates";
    const dist = r.miles_from_boundary === null ? "" : ` · ${r.miles_from_boundary} mi ${r.verdict === "inside" ? "from the edge" : "outside"}`;
    return `${how}${dist}`;
}

function csvOf(rows: Row[]) {
    const head = ["Verdict", "Report", "Follow-up of", "Date", "Species", "Birds", "Address", "Cross streets",
        "City", "ZIP", "County", "WNV", "Status", "Disposition", "Submitted by",
        "Placed", "Latitude used", "Longitude used", "Miles from boundary", "Near the edge", "Note"];
    const esc = (v: unknown) => {
        const s = v === null || v === undefined ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = rows.map((r) => [
        r.verdict, r.id, r.parent, r.date, r.species, r.number, r.address, r.cross,
        r.city, r.zip, r.county, r.wnv, r.status, r.condition, r.agency,
        r.method, r.lat, r.lng, r.miles_from_boundary, r.near_edge ? "yes" : "", r.note,
    ].map(esc).join(","));
    return [head.join(","), ...lines].join("\n");
}

/** `today` is for tests, which need a fixed idea of which month is current. */
export default function CarcassReportCard({ today }: { today?: Date } = {}) {
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [fileName, setFileName] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState<Result | null>(null);
    // "" is every month. Otherwise a key such as "2026-10", or "undated".
    const [month, setMonth] = useState("");

    // Everything below the upload is about the month chosen: the counts, the
    // three tables, and the file that downloads. The server sorted the whole
    // upload once; choosing a month is only a view of that.
    const months = useMemo(() => monthsOf(result?.rows || []), [result]);
    const shown = useMemo(() => inMonth(result?.rows || [], month), [result, month]);
    const counts = useMemo(() => {
        const c = { inside: 0, outside: 0, review: 0, nearEdge: 0 };
        for (const r of shown) {
            if (r.verdict === "inside") c.inside += 1;
            else if (r.verdict === "outside") c.outside += 1;
            else c.review += 1;
            if (r.near_edge) c.nearEdge += 1;
        }
        return c;
    }, [shown]);

    // Step through the months the file has, newest first. Index -1 is "all".
    const step = (by: number) => {
        const at = month ? months.findIndex((m) => m.key === month) : -1;
        const next = at + by;
        if (next < -1 || next >= months.length) return;
        setMonth(next === -1 ? "" : months[next].key);
    };

    // This year so far, unless changed. Local dates: toISOString would be UTC.
    const isoDay = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const [pullFrom, setPullFrom] = useState(() => `${new Date().getFullYear()}-01-01`);
    const [pullTo, setPullTo] = useState(() => isoDay(new Date()));

    const pull = async () => {
        setBusy(true);
        setError("");
        setResult(null);
        setMonth("");
        try {
            const r = await api.post("/carcass-pull", { from: pullFrom, to: pullTo });
            setResult(r.data);
            setMonth(defaultMonth(monthsOf(r.data?.rows || []), today));
            if (r.data?.message) setError(r.data.message);
        } catch (e: any) {
            setError(e?.response?.data?.message || "Could not pull the reports from VectorSurv.");
        } finally {
            setBusy(false);
        }
    };

    const run = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) return;
        setBusy(true);
        setError("");
        setResult(null);
        setMonth("");
        try {
            const form = new FormData();
            form.append("file", file);
            const r = await api.post("/carcass-classify", form);
            setResult(r.data);
            setMonth(defaultMonth(monthsOf(r.data?.rows || []), today));
        } catch (e: any) {
            setError(e?.response?.data?.message || "Could not read that file.");
        } finally {
            setBusy(false);
        }
    };

    const download = () => {
        if (!result) return;
        // What is on screen, not the whole upload: with a month chosen, that
        // month's reports. Named for it, so two downloads do not collide.
        const blob = new Blob([csvOf(shown)], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = month ? `carcass_reports_${month}.csv` : `carcass_reports_sorted.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const table = (title: string, blurb: string, verdict: Row["verdict"], tone: string) => {
        const rows = shown.filter((r) => r.verdict === verdict);
        return (
            <div className="ui-card p-0 overflow-hidden">
                <div className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-extrabold text-[15px]">{title}</h3>
                        <span className={`pill ${tone}`}>{rows.length}</span>
                    </div>
                    <p className="text-[12px] text-ink-faint mt-1">{blurb}</p>
                </div>
                {rows.length > 0 && (
                    <div className="overflow-auto max-h-[26rem] border-t border-line">
                        <table className="w-full text-[13px]">
                            <thead className="bg-paper-2 sticky top-0">
                                <tr>
                                    {["Report", "Date", "Species", "Address", "City", "ZIP", "WNV", "VectorSurv says", "Placed", "Note"].map((h) => (
                                        <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={`${r.id}-${i}`} className="border-t border-line align-top">
                                        <td className="px-3 py-2 font-mono text-[12px] whitespace-nowrap">
                                            {reportUrl(r.id) ? (
                                                <a
                                                    href={reportUrl(r.id)}
                                                    target="_blank"
                                                    rel="noreferrer noopener"
                                                    className="text-brand-deep underline underline-offset-2"
                                                    title="Open this report in VectorSurv, where it can be printed"
                                                >
                                                    {r.id}
                                                </a>
                                            ) : (
                                                r.id || "—"
                                            )}
                                            {r.parent && (
                                                <div className="text-[11px] text-ink-faint">follow-up of {r.parent}</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">{r.date || "—"}</td>
                                        <td className="px-3 py-2">
                                            {r.species || "—"}
                                            {r.number && r.number !== "1" && (
                                                <span className="text-[11px] text-ink-faint"> ×{r.number}</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-ink-soft max-w-[16rem]">
                                            {r.address || "—"}
                                            {r.cross && <div className="text-[11px] text-ink-faint">{r.cross}</div>}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">{r.city || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-[12px]">{r.zip || "—"}</td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {/^pos/i.test(r.wnv) ? (
                                                <span className="pill bg-wnv-tint text-wnv">Positive</span>
                                            ) : (
                                                r.wnv || "—"
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-[12px] text-ink-soft">
                                            {r.condition || "—"}
                                            {(r.status || r.agency) && (
                                                <div className="text-[11px] text-ink-faint">
                                                    {[r.status, r.agency && `submitted by ${r.agency}`].filter(Boolean).join(" · ")}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-[12px] whitespace-nowrap">
                                            {whereText(r)}
                                            {r.near_edge && (
                                                <span className="pill bg-wnv-tint text-wnv ml-2">close to the edge</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-[12px] text-ink-soft max-w-[22rem]">{r.note || ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <div className="ui-card p-4">
                <p className="text-ink-soft text-[13px]">
                    Pull the dead bird reports straight from VectorSurv, or upload its export. Each
                    report is placed on the map from its coordinates, or from its address when the
                    coordinates are missing, and sorted by whether it falls inside the District.
                    Nothing is stored.
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(e) => setFileName(e.target.files?.[0]?.name || "")}
                        className="text-[13px]"
                    />
                    <button onClick={run} disabled={busy || !fileName} className="btn-primary text-sm">
                        {busy ? "Sorting…" : "Sort the reports"}
                    </button>
                </div>
                <div className="flex flex-wrap items-end gap-3 mt-4 pt-4 border-t border-line">
                    <label className="text-[12px] text-ink-soft">
                        From
                        <input type="date" value={pullFrom} onChange={(e) => setPullFrom(e.target.value)}
                            className="block mt-1 px-2 py-1.5 border border-line-strong rounded-sm2 text-[13px] text-ink" />
                    </label>
                    <label className="text-[12px] text-ink-soft">
                        To
                        <input type="date" value={pullTo} onChange={(e) => setPullTo(e.target.value)}
                            className="block mt-1 px-2 py-1.5 border border-line-strong rounded-sm2 text-[13px] text-ink" />
                    </label>
                    <button onClick={pull} disabled={busy || !pullFrom || !pullTo} className="btn-primary text-sm">
                        {busy ? "Working…" : "Pull from VectorSurv"}
                    </button>
                    <span className="text-[12px] text-ink-faint">
                        No file needed. Fresno County reports, straight from VectorSurv; callers' names and numbers are never read.
                    </span>
                </div>
                {error && <p className="text-[13px] text-wnv mt-2">{error}</p>}
            </div>

            {result && (
                <>
                    <div className="ui-card p-4">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            <label htmlFor="carcass-month" className="eyebrow">Month</label>
                            <button
                                onClick={() => step(1)}
                                disabled={month === "" ? months.length === 0 : months.findIndex((m) => m.key === month) >= months.length - 1}
                                className="btn-ghost text-sm !py-1 !px-2"
                                aria-label="Earlier month"
                                title="Earlier month"
                            >‹</button>
                            <select
                                id="carcass-month"
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                                className="text-sm px-2 py-1 border border-line-strong rounded-sm2 bg-card"
                            >
                                <option value="">All months ({result.rows.length})</option>
                                {months.map((m) => (
                                    <option key={m.key} value={m.key}>{m.label} ({m.count})</option>
                                ))}
                            </select>
                            <button
                                onClick={() => step(-1)}
                                disabled={month === ""}
                                className="btn-ghost text-sm !py-1 !px-2"
                                aria-label="Later month"
                                title="Later month (past the newest goes back to all)"
                            >›</button>
                            {month && (
                                <span className="text-[12px] text-ink-faint">
                                    Showing {shown.length} of {result.rows.length} reports from {monthLabel(month)}
                                </span>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                            <div><span className="font-extrabold text-[18px]">{counts.inside}</span> <span className="text-ink-soft text-[13px]">inside the District</span></div>
                            <div><span className="font-extrabold text-[18px]">{counts.outside}</span> <span className="text-ink-soft text-[13px]">outside</span></div>
                            <div><span className="font-extrabold text-[18px]">{counts.review}</span> <span className="text-ink-soft text-[13px]">need a look</span></div>
                            {counts.nearEdge > 0 && (
                                <div className="text-[12px] text-wnv">{counts.nearEdge} within a quarter mile of the boundary</div>
                            )}
                            <button onClick={download} className="btn-ghost text-sm !py-1 ml-auto">
                                {month ? `Download ${monthLabel(month)} (CSV)` : "Download sorted CSV"}
                            </button>
                        </div>
                        <p className="text-[12px] text-ink-faint mt-2">
                            Columns read:{" "}
                            {Object.entries(result.columns).map(([role, col]) => `${ROLE_LABEL[role] || role} = “${col}”`).join(" · ") || "none recognised"}
                            {". "}If one is wrong or missing, the headings in the file are:{" "}
                            {result.fieldnames.join(", ")}.
                        </p>
                    </div>

                    {table("Inside the District", "These are the ones to follow up.", "inside", "bg-sle-tint text-sle")}
                    {table("Outside the District", "Placed with confidence and not within the boundary.", "outside", "bg-paper-2 text-ink-soft")}
                    {table("Needs a look", "Not placed with confidence, so not counted as outside: no usable coordinates, an address that could not be found, or coordinates that had to be corrected.", "needs a look", "bg-wnv-tint text-wnv")}
                </>
            )}

            <div className="ui-card p-4">
                <div className="eyebrow mb-3">Dead bird report watcher</div>
                <CarcassWatchStatus />
            </div>
        </div>
    );
}
